/**
 * <InlineLink /> — renders a `render: "link"` tool result inside the
 * chat panel (M22 + PR2b hybrid auto-navigate).
 *
 * The navigate_to / open_entity_drawer tools propose a destination.
 *
 * Hybrid behavior (PR2b):
 *  - data.auto_navigate === true → the panel navigates the user to
 *    the URL on mount, then shows a confirmation chip ("Opened
 *    {label}") so the user can see what happened. The chat panel
 *    stays open.
 *  - default → renders the click button ("Go to {label}"). The chat
 *    proposes, the human navigates.
 *
 * Tool result shape: data = { url, label, auto_navigate? }.
 */

import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export function InlineLink({
  summary,
  data,
}: {
  summary: string;
  data: Record<string, unknown>;
}) {
  const navigate = useNavigate();
  const url = typeof data.url === "string" ? data.url : null;
  const label =
    typeof data.label === "string" ? data.label : "Open";
  const autoNavigate = data.auto_navigate === true;

  const [hasAutoNavigated, setHasAutoNavigated] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (!autoNavigate || !url || fired.current) return;
    fired.current = true;
    navigate(url);
    setHasAutoNavigated(true);
  }, [autoNavigate, url, navigate]);

  if (!url) {
    return (
      <div className="rounded-lg border border-border-subtle bg-surface-muted px-2.5 py-2 text-xs text-text-secondary">
        {summary}
      </div>
    );
  }

  if (autoNavigate && hasAutoNavigated) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-300/60 bg-emerald-50 px-2.5 py-2 text-xs text-emerald-900">
        <CheckCircle2 size={13} className="shrink-0" />
        <span>
          Opened <span className="font-medium">{label}</span>
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate(url)}
      className="flex w-full items-center justify-between gap-2 rounded-lg border border-brand/40 bg-brand/5 px-2.5 py-2 text-start text-xs hover:bg-brand/10"
    >
      <span className="text-text-primary">
        Go to <span className="font-medium">{label}</span>
      </span>
      <ArrowRight
        size={13}
        className="shrink-0 text-brand rtl:rotate-180"
      />
    </button>
  );
}
